import { useCallback, useMemo, useState } from "react";
import tasks from "@/assets/data/json_output/tasks.json";
import type { TaskTableRow } from "@/views/Risk/Tasks/utils";

/**
 * Task rows minus the ones the user has removed. Removal is demo-local state
 * kept in localStorage, and it is shared by the Quality > Tasks screen and the
 * top-level Actions screen: both render the same underlying records, so
 * removing one there has to remove it here too.
 */
const HIDDEN_KEY = "vivo_hidden_tasks";
const allTaskRows = tasks as TaskTableRow[];

function loadHidden(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveHidden(hidden: Set<string>) {
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden]));
  } catch {
    /* storage unavailable */
  }
}

export function useHiddenTasks() {
  const [hidden, setHidden] = useState<Set<string>>(loadHidden);

  const taskRows = useMemo(
    () => allTaskRows.filter((task) => !hidden.has(String(task.task_id))),
    [hidden],
  );

  const hideTask = useCallback((task_id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.add(String(task_id));
      saveHidden(next);
      return next;
    });
  }, []);

  return { taskRows, hideTask };
}
